// import { useRootElement } from "@/providers/RootElement/hooks";
import { useOutsideClick } from "@/providers/RootElement/hooks/events";
import { color } from "@/styles/tokens";
import { ArrowUp, ChevronRightThick } from "@/svg";
import {
	Select,
	SelectArrow,
	SelectItem,
	SelectLabel,
	SelectPopover,
	SelectProvider,
} from "@ariakit/react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { SVGIcon } from "hsl-core/Components";
import { useRef, useState } from "react";
import * as styles from "./MonthPicker.css";
import type { Props } from "./types";

dayjs.extend(customParseFormat);
export const parseDate = (dateStr: string) =>
	dayjs(dateStr, ["MMM YYYY", "YYYY-M-DD", "YYYY-M-D", "YYYY MMM"]).toDate();

export default function MonthPicker({
	availableMonths,
	value = parseDate(`${availableMonths[0][1].at(-1)} ${availableMonths[0][0]}`),
	onChange,
	label = "Select month",
	hideButton = false,
}: Props) {
	// const root = useRootElement();
	const monthsMap = Object.fromEntries(availableMonths);
	const years = availableMonths.map(([year]) => Number.parseInt(year));
	const toLowerCase = (str: string) => str.toLowerCase();
	const toSelectValue = (date: Date) =>
		`${date.getFullYear()}-${date.getMonth() + 1}-01`;
	const toOptionValue = (year: string, month: string) => {
		const monthIndex = months.indexOf(month.toLowerCase());
		return `${year}-${monthIndex + 1}-01`;
	};
	const months = [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"May",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Oct",
		"Nov",
		"Dec",
	].map(toLowerCase);
	const valueYear = value.getFullYear();
	const valueMonth = value.getMonth();
	const [open, setOpen] = useState(false);
	const [year, setYear] = useState(valueYear);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);
	const hasYear = (y: number) => years.includes(y);
	const chevron = (
		<SVGIcon icon={ChevronRightThick} params={{ fill: color.text.primary }} />
	);
	const popoverContent = (
		<>
			<div className={styles.year}>
				<button
					type="button"
					disabled={!hasYear(year - 1)}
					onClick={() => setYear(year - 1)}
					className={styles.yearButton({ type: "prev" })}
				>
					{chevron}
				</button>
				{year}
				<button
					type="button"
					disabled={!hasYear(year + 1)}
					onClick={() => setYear(year + 1)}
					className={styles.yearButton({ type: "next" })}
				>
					{chevron}
				</button>
			</div>
			<div className={styles.grid}>
				{months.map((month) => (
					<SelectItem
						key={`${year}-${month}`}
						value={toOptionValue(`${year}`, month)}
						className={styles.item}
						disabled={!monthsMap[year]?.map(toLowerCase).includes(month)}
					>
						{month}
					</SelectItem>
				))}
			</div>
		</>
	);

	useOutsideClick([buttonRef, popoverRef], () => setOpen(false));
	// const portalElement = document.createElement("div", {});
	// portalElement.style.position = "relative";
	// portalElement.style.zIndex = "99999";
	// root.appendChild(portalElement);

	return (
		<SelectProvider
			setValueOnMove
			open={open || hideButton}
			setOpen={setOpen}
			value={toSelectValue(value)}
			setValue={(dateStr) => {
				onChange?.(parseDate(dateStr));
			}}
		>
			{label && <SelectLabel className={styles.label}>{label}</SelectLabel>}
			{!hideButton && (
				<Select className={styles.select} ref={buttonRef}>
					{months[valueMonth]} {valueYear}{" "}
					<SelectArrow
						render={() => (
							<SVGIcon icon={ArrowUp} className={styles.arrow({ open })} />
						)}
					/>
				</Select>
			)}
			{hideButton ? (
				popoverContent
			) : (
				<SelectPopover className={styles.popover} ref={popoverRef}>
					{popoverContent}
				</SelectPopover>
			)}
		</SelectProvider>
	);
}
